import { Store } from '@reduxjs/toolkit';
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { AuthenticationService } from './generated/services/AuthenticationService';
import { logout, refreshUserData } from '@/store/authSlice';
import { getStoredTokens, saveTokens, clearTokens } from '@/store/authSlice';
import type { RootState, AppDispatch } from '@/store';

interface QueueItem {
  resolve: (value?: InternalAxiosRequestConfig) => void;
  reject: (error?: Error) => void;
  config: InternalAxiosRequestConfig;
}

class TokenRefreshManager {
  public isRefreshing = false;
  public failedQueue: QueueItem[] = [];
  private store: Store<RootState> | null = null;

  setStore(store: Store<RootState>) {
    this.store = store;
  }

  processQueue(error?: Error, token?: string) {
    this.failedQueue.forEach(({ resolve, reject, config }) => {
      if (error) {
        reject(error);
      } else {
        // Update config with new token and resolve
        if (config && token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        resolve(config);
      }
    });

    this.failedQueue = [];
  }

  async handleTokenRefresh(): Promise<string> {
    const { refreshToken: storedRefreshToken } = getStoredTokens();

    if (!storedRefreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await AuthenticationService.appsAuthenticationViewsRefreshTokenView({
        requestBody: { refresh_token: storedRefreshToken }
      });

      // Save new tokens (only access_token available)
      saveTokens(response.access_token, storedRefreshToken);

      // Refresh user data after successful token refresh
      if (this.store) {
        try {
          // Get current workspace org_id from store
          const state = this.store.getState();
          const orgId = state.workspace.selectedWorkspace?.org_id;

          // Dispatch refreshUserData action to update localStorage and Redux state
          console.log('🔄 Refreshing user data after token refresh...');
          await (this.store.dispatch as AppDispatch)(refreshUserData(orgId));
          console.log('✅ User data refreshed successfully after token refresh');
        } catch (userDataError) {
          // Log warning but don't fail token refresh - user data refresh is non-critical
          console.warn('⚠️ Failed to refresh user data after token refresh:', userDataError);
        }
      }

      return response.access_token;
    } catch (error) {
      // Clear tokens and logout on refresh failure
      clearTokens();
      if (this.store) {
        (this.store.dispatch as AppDispatch)(logout());
      }
      throw error;
    }
  }
}

// Create singleton instance
export const tokenRefreshManager = new TokenRefreshManager();

// Setup axios interceptors function
export function setupApiInterceptors(store: Store<RootState>) {
  tokenRefreshManager.setStore(store);

  // Request interceptor to add token
  axios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const { accessToken } = getStoredTokens();
      if (accessToken && config.headers) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle 401 errors and refresh tokens
  axios.interceptors.response.use(
    (response: AxiosResponse) => {
      // Return successful responses as-is
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Check if error is 401 and not already retrying
      if (error.response?.status === 401 && !originalRequest._retry) {
        console.log('🔑 401 error detected, attempting token refresh...', originalRequest.url);
        
        // Avoid infinite loops on refresh endpoint
        if (originalRequest.url?.includes('/auth/refresh')) {
          console.log('⚠️ Refresh endpoint failed, skipping refresh');
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        if (tokenRefreshManager.isRefreshing) {
          console.log('🔄 Token refresh already in progress, queueing request...');
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            tokenRefreshManager.failedQueue.push({
              resolve,
              reject,
              config: originalRequest
            });
          });
        }

        tokenRefreshManager.isRefreshing = true;
        console.log('🚀 Starting token refresh process...');

        try {
          const newToken = await tokenRefreshManager.handleTokenRefresh();
          console.log('✅ Token refresh successful, retrying original request...');
          tokenRefreshManager.processQueue(null, newToken);
          
          // Retry the original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          
          return axios(originalRequest);
        } catch (refreshError) {
          console.log('❌ Token refresh failed:', refreshError);
          tokenRefreshManager.processQueue(refreshError);
          return Promise.reject(refreshError);
        } finally {
          tokenRefreshManager.isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
}

// Utility function to manually refresh token
export async function refreshTokenIfNeeded(): Promise<boolean> {
  const { accessToken } = getStoredTokens();

  if (!accessToken) {
    return false;
  }

  try {
    // Try to decode JWT to check expiration (optional)
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    const bufferTime = 300; // 5 minutes buffer

    if (payload.exp && payload.exp < currentTime + bufferTime) {
      await tokenRefreshManager.handleTokenRefresh();
      return true;
    }
  } catch (error) {
    console.warn('Token validation failed:', error);
    // If token parsing fails, let the API call proceed and handle 401 if needed
  }

  return false;
}